import { useEffect, useState, useMemo } from 'react';
import { PackageContext } from '../context/PackageContext';
import { useStateValue } from '../hooks/UseStateValue';
import { useConfigContext } from '../context/ConfigContext';
import { mappedPackages } from '../helpers/packages';
import { Package, SelectedAdditionalCoverage } from '../interfaces/interfaces';
import { setSelectionValue } from '../store/Actions';

type Props = {
    children: React.ReactElement;
};

const PackageProvider = ({ children }: Props) => {
    const [{ selection, model }, dispatch] = useStateValue();
    const config = useConfigContext();
    const [selectedPackage, setSelectedPackage] = useState<Package>({} as Package);
    const [selectedDeductible, setSelectedDeductible] = useState<number>(0);
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(1);
    const [packages, setPackages] = useState<Package[]>([]);
    const [additionalCoverages, setAdditionalCoverages] = useState<string[]>([]);
    const [selectedAdditionalCoverages, setSelectedAdditionalCoverages] = useState<SelectedAdditionalCoverage[]>([]);
    const [didInitSelectedPackage, setDidInitSelectedPackage] = useState<boolean>(false);

    useEffect(() => {
        // initialize the selection.package on first load
        const mcpp = model[config.product].packages;
        if (mcpp && !didInitSelectedPackage) {
            setDidInitSelectedPackage(true);

            const defaultPackage = mcpp.packages.find((p) => p.defaultSelected);
            setSelectionValue(dispatch, selection, { key: 'package', value: defaultPackage }, true);
        }
    }, [config.product, didInitSelectedPackage, dispatch, model, selection]);

    useEffect(() => {
        const mcpp = model[config.product].packages;
        if (mcpp) {
            const p = mcpp.packages.find((d) => d.defaultSelected);

            const selectedPackage = selection.package ?? p;
            setSelectedPackage(selectedPackage!);

            const packages = mappedPackages(mcpp.packages);
            const index = packages.map((p) => p.id).indexOf((selectedPackage as Package).quoteId);

            setSelectedPackageIndex(index > -1 ? index : 1);

            setPackages(mcpp.packages);

            setAdditionalCoverages(mcpp.additionalCoverages); // "available" additional coverages (not selected)

            setSelectedAdditionalCoverages(selection.selectedAdditionalCoverages ?? []); // selected additional coverages
        }
    }, [config.product, model, selection.package, selection.selectedAdditionalCoverages]);

    useEffect(() => {
        const defaultDeductible =
            //TODO: set to deductibleBasis when BE is fixed
            model[config.product].packages?.deductibles?.find((d) => d.defaultDeductible)?.amount ?? 0;
        setSelectedDeductible(selection.deductible ?? defaultDeductible);
    }, [model, selection, config.product]);

    type PackageProvider = {
        selectedPackage: Package;
        selectedPackageIndex: number;
        selectedDeductible: number;
        packages: Package[];
        additionalCoverages: string[];
        selectedAdditionalCoverages: SelectedAdditionalCoverage[];
    };

    const items: PackageProvider = useMemo(
        () => ({
            selectedPackage,
            selectedPackageIndex,
            selectedDeductible,
            packages,
            additionalCoverages,
            selectedAdditionalCoverages
        }),
        [
            additionalCoverages,
            selectedAdditionalCoverages,
            packages,
            selectedDeductible,
            selectedPackage,
            selectedPackageIndex
        ]
    );

    return <PackageContext.Provider value={items}>{children}</PackageContext.Provider>;
};

export default PackageProvider;
